import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Target, Award, BookOpen, Trophy } from 'lucide-react';
import WhyChooseSection from '../components/WhyChooseSection';
import SEO from '../components/SEO';
import { getFaqSchema, getFaqItems } from '../components/schema';

const Home = () => {
  const faqItems = getFaqItems();
  const faqSchema = getFaqSchema();

  const features = [
    { icon: Users, title: '1:1 Accountability', desc: 'Personal daily progress tracking' },
    { icon: Target, title: 'No Bench-Warmers', desc: "We don't move forward until you master it" },
    { icon: Award, title: '100% Practical', desc: 'Hands-on training for real skills' },
    { icon: BookOpen, title: 'Job-Ready Skills', desc: 'Learn what employers actually need' },
  ];

  const testimonials = [
    { name: 'Priya Sharma', course: 'Tally with GST', text: 'Best decision ever! The personal attention helped me get a job within 2 months.' },
    { name: 'Rahul Kumar', course: 'Programming', text: 'Unlike other institutes, here they actually care if you learn. Worth every rupee!' },
    { name: 'Anjali Patil', course: 'MS Office', text: 'I was struggling with Excel. The instructor patiently taught me until I got it perfect.' },
  ];

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Best Computer Training Institute in Kalaburagi (Gulbarga)"
        description="Genius Academy is the leading computer institute in Kalaburagi (Gulbarga) offering MS Office, Tally with GST, Python, Web Development, Photoshop, CorelDRAW, Basic Computer, Typing and Computer Hardware courses with 1:1 accountability and hands-on training."
        path="/"
        image="https://geniusacademyklbg.com/images/computer-lab.webp"
        keywords="Computer Institute in Gulbarga, Computer Classes in Kalaburagi, Computer Training Institute, MS Office Course, Tally Course, Basic Computer Course, Typing Classes, Python Course, Web Development Course, Photoshop Course, CorelDRAW Course, Computer Hardware Course"
        jsonLd={faqSchema}
        breadcrumbs={[{ name: 'Home', path: '/' }]}
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-cyan-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-block bg-orange-100 text-orange-600 px-4 py-2 rounded-full text-sm font-bold mb-6">
                🔥 Limited Seats Available
              </div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-4" data-testid="hero-headline">
                Don't Just Learn Computers. <span className="text-cyan-600">Master Them.</span>
              </h1>
              <p className="text-lg text-slate-700 font-semibold mb-4">
                Genius Academy - Technical and Computer Training Institute
              </p>
              <p className="text-base md:text-lg text-slate-600 mb-8 leading-relaxed">
                At Genius Academy, you're not just another face in a batch of 50. I personally track your progress daily. 
                We don't move forward until you master each module. <strong>No bench-warmers here.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full px-8 py-4 font-bold shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                  data-testid="hero-cta-book-demo"
                >
                  Book Free Demo <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="https://wa.me/919164998997"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-full px-8 py-4 font-bold transition-all"
                  data-testid="hero-cta-whatsapp"
                >
                  WhatsApp Us
                </a>
              </div>

              {/* Premium Smart System Badge */}
              <motion.a
                href="#why-choose"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="group mt-8 flex items-start gap-3 max-w-xl rounded-2xl p-4 md:p-5 backdrop-blur-xl bg-white/70 border border-white/70 shadow-[0_8px_24px_-12px_rgba(8,145,178,0.35)] hover:shadow-[0_14px_36px_-12px_rgba(8,145,178,0.5)] hover:border-cyan-200 transition-all"
                data-testid="hero-smart-system-badge"
              >
                <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 via-orange-500 to-pink-500 flex items-center justify-center shadow-md shadow-orange-500/30 group-hover:scale-110 transition-transform">
                  <Trophy className="w-5 h-5 text-white" strokeWidth={2.5} />
                </span>
                <span className="text-left">
                  <span className="block font-bold text-slate-900 text-sm md:text-base leading-snug">
                    Powered by Our Own Smart Student Management System
                  </span>
                  <span className="block text-xs md:text-sm text-slate-600 leading-relaxed mt-1">
                    A unique in-house technology built exclusively by Genius Academy for a secure, disciplined and professionally managed learning experience.
                  </span>
                </span>
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <img
                src="/images/computer-lab.webp"
                alt="Students learning computer courses at Genius Academy computer lab, Kalaburagi"
                className="rounded-2xl shadow-2xl"
                width="1265"
                height="569"
                loading="eager"
                fetchpriority="high"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-bold text-slate-900">100% Practical</p>
                    <p className="text-sm text-slate-600">Real Skills, Real Results</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Courses Preview */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4" data-testid="courses-section-title">
              Our Courses
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              From basics to advanced, we cover everything you need for a successful career.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <CoursePreviewCard title="Computer Fundamentals & Typing" desc="Master basics & typing" href="/courses/typing" />
            <CoursePreviewCard title="MS Office Suite" desc="Word, Excel, PowerPoint" href="/courses/ms-office" />
            <CoursePreviewCard title="Tally with GST" desc="Complete accounting" href="/courses/tally-gst" />
            <CoursePreviewCard title="Python Programming" desc="Learn Python from scratch" href="/courses/python" />
            <CoursePreviewCard title="Photoshop" desc="Photo editing & design" href="/courses/photoshop" />
            <CoursePreviewCard title="Computer Hardware" desc="Assembly & repair" href="/courses/computer-hardware" />
          </div>

          <div className="text-center mt-12">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-4 font-bold transition-all"
              data-testid="view-all-courses-button"
            >
              View All Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Why Genius Academy */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Why Genius Academy?
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              We're not just another computer institute. Here's what makes us different.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                  data-testid={`feature-${index}`}
                >
                  <div className="bg-cyan-100 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-cyan-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Premium: Why Choose Genius Academy */}
      <WhyChooseSection />

      {/* Comparison Table */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Genius Academy vs Others
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-bold">Feature</th>
                  <th className="px-6 py-4 text-center font-bold">Genius Academy</th>
                  <th className="px-6 py-4 text-center font-bold">Other Institutes</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-slate-100">
                  <td className="px-6 py-4 font-medium">Personal Tracking</td>
                  <td className="px-6 py-4 text-center"><span className="text-green-500 text-2xl">✓</span></td>
                  <td className="px-6 py-4 text-center"><span className="text-red-500 text-2xl">✗</span></td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <td className="px-6 py-4 font-medium">Small Batches</td>
                  <td className="px-6 py-4 text-center"><span className="text-green-500 text-2xl">✓</span></td>
                  <td className="px-6 py-4 text-center"><span className="text-red-500 text-2xl">✗</span></td>
                </tr>
                <tr className="border-b border-slate-100">
                  <td className="px-6 py-4 font-medium">Owner as Instructor</td>
                  <td className="px-6 py-4 text-center"><span className="text-green-500 text-2xl">✓</span></td>
                  <td className="px-6 py-4 text-center"><span className="text-red-500 text-2xl">✗</span></td>
                </tr>
                <tr className="bg-slate-50">
                  <td className="px-6 py-4 font-medium">100% Practical</td>
                  <td className="px-6 py-4 text-center"><span className="text-green-500 text-2xl">✓</span></td>
                  <td className="px-6 py-4 text-center"><span className="text-red-500 text-2xl">✗</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              What Our Students Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 shadow-sm"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-slate-700 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-bold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-slate-600">{testimonial.course}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section (SEO + AI search) */}
      <section className="py-16 md:py-24" aria-labelledby="faq-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 id="faq-heading" className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Everything you need to know about our computer training institute in Kalaburagi (Gulbarga).
            </p>
          </div>
          <div className="space-y-3" itemScope itemType="https://schema.org/FAQPage">
            {faqItems.map((item, i) => (
              <details
                key={i}
                className="group bg-white border border-slate-100 rounded-2xl shadow-sm open:shadow-md open:border-cyan-200 transition-all"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between p-5 md:p-6 font-semibold text-slate-900">
                  <span itemProp="name">{item.q}</span>
                  <span className="ml-4 text-cyan-600 group-open:rotate-45 transition-transform text-2xl leading-none" aria-hidden="true">+</span>
                </summary>
                <div
                  className="px-5 md:px-6 pb-5 md:pb-6 text-slate-600 leading-relaxed"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Limited Seats – We Teach Until You Learn
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Join hundreds of students who got job-ready skills with our 1:1 accountability model.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 font-bold shadow-lg transition-all"
                data-testid="final-cta-contact"
              >
                Enroll Now <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:9164998997"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all"
                data-testid="final-cta-call"
              >
                Call 9164998997
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

const CoursePreviewCard = ({ title, desc, href }) => {
  const linkTo = href || '/courses';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-cyan-200 transition-all duration-300 p-6 hover:-translate-y-1"
    >
      <div className="bg-cyan-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
        <BookOpen className="w-7 h-7 text-cyan-600" />
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">{title}</h3>
      <p className="text-slate-600 mb-4">{desc}</p>
      <Link
        to={linkTo}
        className="text-cyan-600 font-medium hover:text-cyan-700 inline-flex items-center gap-1"
      >
        Learn More <ArrowRight size={16} />
      </Link>
    </motion.div>
  );
};

export default Home;